import { BrandKey, brandKeysByUnit } from '@prismamedia/one-brandkey';
import {
  AutocompleteItem,
  getImageFromMedias,
  MediaTypesEnum,
} from '@prismamedia/one-components';
import { convertFromRaw, EditorState, RawDraftContentState } from 'draft-js';
import { path } from 'ramda';
import {
  GetCampaignsBlocksQuery,
  GetCampaignsBlocksQuery_blocks_Block_section_Section_campaign_Campaign,
} from '../../../../../__generated__/queries-newsletter';
import {
  RecipeOrderByInput,
  RecipeStatus,
} from '../../../../../__generated__/queries-recipe';
import {
  ArticleFilterSearchInput,
  ArticleFormat,
  ArticleOrderingSearchInput,
  ArticleStatus,
} from '../../../../../__generated__/queries-web';
import { useGetArticles } from '../../../../../apollo/queries/articles.web.graphql';
import { useGetCampaignsBlocks } from '../../../../../apollo/queries/getCampaigns.newsletter.graphql';
import { useGetRecipes } from '../../../../../apollo/queries/recipes.recipe.graphql';
import { extractPath } from '../../../../../utils/extractPath';
import { getImageFromSlideShow } from '../../../../../utils/media';
import { BlockItemType } from '../Items/utils';

export const NO_SOURCE_ID = 'NO_SOURCE_ID';
export const PAID_ARTICLE = 'Article payant';

export const PAGINATION = 50;

export enum ResourceType {
  EMPTY = 'EMPTY',
  ARTICLE = 'ARTICLE',
  DIAPO = 'DIAPO',
  RECIPE = 'RECIPE',
}

export interface Resource {
  type: ResourceType;
  label: string;
  placeholder: string;
  allowedBrands?: string[];
}

export const resources: Resource[] = [
  {
    type: ResourceType.ARTICLE,
    label: 'Article',
    placeholder: 'Rechercher un article',
  },
  {
    type: ResourceType.DIAPO,
    label: 'Diapo',
    placeholder: 'Rechercher un diapo',
  },
  {
    type: ResourceType.RECIPE,
    label: 'Recette',
    placeholder: 'Rechercher une recette',
    allowedBrands: ['FAC', 'CAC', 'TRV'],
  },
];

interface DiapoSearchParams {
  search?: string;
  categories?: AutocompleteItem[];
  tags?: AutocompleteItem[];
  source?: string;
  creator?: AutocompleteItem;
  updatedAfter?: Date;
  updatedBefore?: Date;
  imageSource?: AutocompleteItem;
  imageCredit?: string;
}
interface ArticleSearchParams extends Omit<DiapoSearchParams, 'imageCredit'> {
  author?: AutocompleteItem;
  containPush?: boolean;
  containLive?: boolean;
  containVideo?: boolean;
  containDiapo?: boolean;
  qualifier?: string;
}

interface RecipeSearchParams {
  search?: string;
  mainCategory?: string;
  difficulty?: string;
  calorieLevel?: string;
  estimatedCost?: string;
  maxPrepTime?: number;
  maxCookTime?: number;
  maxWaitTime?: number;
  ingredients?: string[];
  tags?: string[];
}

export interface SearchParams
  extends ArticleSearchParams,
    DiapoSearchParams,
    RecipeSearchParams {
  tags?: (AutocompleteItem | string | any)[];
}

const getArticleUrl = (articleUrls: any): string => {
  if (articleUrls.length) {
    const [first] = articleUrls;
    let { origin } = first;
    if (origin.endsWith('/') && first?.path.startsWith('/')) {
      origin = origin.substring(0, origin.length - 1);
    }
    return `${origin}${first?.path}`;
  }
  return '';
};

const baseRecipesURLs: Record<string, string> = {
  FAC: 'https://www.femmeactuelle.fr',
  CAC: 'https://www.cuisineactuelle.fr',
};

const getRecipeUrl = (
  brandKey: string,
  recipeUrls: Record<string, any>,
): string | null => {
  const baseUrl = baseRecipesURLs[brandKey];
  if (baseUrl) {
    const recipeUrl =
      path(['edges', '0', 'node', 'url', 'path'], recipeUrls) ||
      recipeUrls[0]?.url.path;
    if (recipeUrl) {
      return `${baseUrl}${recipeUrl}`;
    }
    return null;
  }
  return null;
};

const getBlockItemAttachedCampaigns = (
  blockItem: BlockItemType,
  campaignsData: GetCampaignsBlocksQuery | undefined,
) =>
  campaignsData?.blocks.reduce(
    (
      filteredBlockItems: GetCampaignsBlocksQuery_blocks_Block_section_Section_campaign_Campaign[],
      { url, section },
    ) => {
      if (blockItem.url === url && section) {
        // eslint-disable-next-line no-param-reassign
        filteredBlockItems = [...filteredBlockItems, section.campaign];
      }
      return filteredBlockItems;
    },
    [],
  );

export const useGetBlockItems = (
  articleFormat: ArticleFormat | undefined,
  resourceType: ResourceType,
  brandKey: BrandKey | null | undefined,
  searchParams: SearchParams,
  campaignAvailability: string | null | undefined,
) => {
  const isSlideshow = resourceType === ResourceType.DIAPO;
  const searchUrlPath = extractPath(searchParams.search || '');

  // FIXME typing will be accurate when api-article will implement new auth
  const brandKey_in: any =
    brandKey === BrandKey.TRV
      ? undefined
      : brandKey === BrandKey.FRGTS
      ? brandKeysByUnit[brandKey].filter((bk) => bk !== BrandKey.FRGTS)
      : [brandKey];

  let editedAfterDate: Date | undefined;
  if (searchParams.updatedAfter) {
    editedAfterDate = new Date(searchParams.updatedAfter);
    editedAfterDate?.setHours(0);
    editedAfterDate?.setMinutes(0);
    editedAfterDate?.setSeconds(0);
  }

  let editedBeforeDate: Date | undefined;
  if (searchParams.updatedBefore) {
    editedBeforeDate = new Date(searchParams.updatedBefore);
    editedBeforeDate?.setHours(23);
    editedBeforeDate?.setMinutes(59);
    editedBeforeDate?.setSeconds(59);
  }

  const articlesWhere: ArticleFilterSearchInput = {
    [articleFormat === ArticleFormat.Video
      ? 'channels_some'
      : 'categories_some']: searchParams.categories
      ? {
          category: { id_in: searchParams.categories.map(({ id }) => id) },
        }
      : undefined,
    authors_some: searchParams.author
      ? { author: { id: searchParams.author.id } }
      : undefined,
    urls_some: searchUrlPath ? { path: searchUrlPath } : undefined,
    tags_some: searchParams.tags
      ? { tag: { id_in: searchParams.tags.map(({ id }) => id) } }
      : undefined,
    brandKey_in,
    creatorId: searchParams.creator?.id,
    format: isSlideshow ? ArticleFormat.Slideshow : ArticleFormat.Rich,
    publishedAt_lt: campaignAvailability
      ? `${campaignAvailability}T23:59:59Z`
      : undefined,
    source:
      searchParams.source && searchParams.source !== NO_SOURCE_ID
        ? { id: searchParams.source }
        : undefined,
    title: searchUrlPath ? undefined : searchParams.search || undefined,
    editedAt_gte: editedAfterDate?.toISOString(),
    editedAt_lte: editedBeforeDate?.toISOString(),
    live_is_null: searchParams.containLive ? false : undefined,
    pushes_some: searchParams.containPush ? {} : undefined,
    qualifiers_some: searchParams.qualifier
      ? { qualifier: { id: searchParams.qualifier } }
      : undefined,
    AND: [
      ...(searchParams.imageSource?.label &&
      typeof searchParams.imageSource.label === 'string'
        ? [
            {
              medias_source: searchParams.imageSource.label,
            },
          ]
        : []),
      ...(searchParams.imageCredit
        ? [
            {
              medias_credit: searchParams.imageCredit,
            },
          ]
        : []),
      ...(searchParams.containDiapo
        ? [
            {
              medias_site: MediaTypesEnum.SLIDESHOW_TYPE,
            },
          ]
        : []),
    ],
    ...(searchParams.containVideo
      ? {
          OR: [
            {
              medias_site: MediaTypesEnum.BRIGHTCOVE_TYPE,
            },
            {
              medias_site: MediaTypesEnum.DAILYMOTION_TYPE,
            },
            {
              medias_site: MediaTypesEnum.YOUTUBE_TYPE,
            },
          ],
        }
      : {}),
  };

  const {
    data: articlesData,
    loading: articlesLoading,
    error: articlesError,
    fetchMore: articleFetchMore,
  } = useGetArticles(
    {
      wherePublishedArticles: {
        ...articlesWhere,
        status: ArticleStatus.Published,
      },
      first: PAGINATION,
      whereScheduledArticles: {
        ...articlesWhere,
        status: ArticleStatus.Scheduled,
      },
      whereScheduledForRepublicationArticles: {
        ...articlesWhere,
        status: ArticleStatus.ScheduledForRepublication,
      },
      orderBy: [ArticleOrderingSearchInput.publishedAt_DESC],
    },
    ![ResourceType.ARTICLE, ResourceType.DIAPO].includes(resourceType),
  );

  const {
    data: recipesData,
    loading: recipesLoading,
    error: recipeError,
    fetchMore: recipeFetchMore,
  } = useGetRecipes(
    {
      first: PAGINATION,
      where: {
        brandKey: brandKey === 'TRV' ? undefined : brandKey,
        publishedAt_lt: campaignAvailability
          ? `${campaignAvailability}T23:59:59Z`
          : undefined,
        status_in: [RecipeStatus.Published, RecipeStatus.Scheduled],
        title_contains: searchUrlPath
          ? undefined
          : searchParams.search || undefined,
        recipeUrls_some: searchUrlPath
          ? { url: { path: searchUrlPath } }
          : undefined,
        recipeCategories_some: searchParams.mainCategory
          ? {
              category: { id: searchParams.mainCategory },
              position: 1,
            }
          : undefined,
        difficulty: searchParams.difficulty,
        estimatedCost: searchParams.estimatedCost,
        calorieLevel: searchParams.calorieLevel,
        prepTime_lte: searchParams.maxPrepTime,
        cookTime_lte: searchParams.maxCookTime,
        waitTime_lte: searchParams.maxWaitTime,
        AND: [
          ...(searchParams.tags?.map((tag) => ({
            recipeTags_some: {
              tag: {
                title: tag,
              },
            },
          })) || []),
          ...(searchParams.ingredients?.map((ingredient) => ({
            parsedIngredients_contains: `"referenceIngredient":"${ingredient}"`,
          })) || []),
        ],
      },
      orderBy: [RecipeOrderByInput.publishedAt_DESC],
    },
    resourceType !== ResourceType.RECIPE,
  );

  switch (resourceType) {
    case ResourceType.ARTICLE:
    case ResourceType.DIAPO: {
      const blockItems = [
        ...(articlesData?.publishedArticles || []),
        ...(articlesData?.scheduledArticles || []),
        ...(articlesData?.scheduledForRepublicationArticles || []),
      ].map(
        (article): BlockItemType => ({
          articleAuthors: article.authors || [],
          articleCategories: article.categories || [],
          articleChannels: article.channels || [],
          articleCreatorData: article.creator,
          articleEvents: article.events || [],
          articleQualifiers: article.qualifiers || [],
          blockType: isSlideshow ? 'Diapo' : 'Article',
          brandKey: article.brandKey,
          format: article.format,
          editedAt: article.editedAt,
          id: article.id,
          liveCount: article.live?.postCount || 0,
          publishedAt: article.publishedAt,
          status: article.status,
          surTitle: article.secondaryTitle,
          title: article.partnerTitle || article.title,
          content:
            article.lead &&
            EditorState.createWithContent(convertFromRaw(article.lead)),
          medias: article.medias,
          pushCount: article.pushCount,
          ...(isSlideshow
            ? getImageFromSlideShow(article.body)
            : getImageFromMedias(article.medias)),
          url: getArticleUrl(article.urls),
        }),
      );
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { data: campaignsData } = useGetCampaignsBlocks(blockItems || []);

      return {
        blockItems: blockItems?.map((blockItem: BlockItemType) => ({
          ...blockItem,
          campaigns: getBlockItemAttachedCampaigns(blockItem, campaignsData),
        })),
        loading: articlesLoading,
        error: articlesError,
        fetchMore: articleFetchMore,
      };
    }
    case ResourceType.RECIPE: {
      const blockItems = recipesData?.recipes.map(
        (recipe): BlockItemType => {
          let medias = [];
          try {
            medias = JSON.parse(recipe.medias || '');
          } catch (e) {}

          let lead = null;
          try {
            lead = JSON.parse(recipe.lead || '') as RawDraftContentState;
          } catch (e) {}

          return {
            blockType: 'Recette',
            brandKey: recipe.brandKey,
            id: recipe.id,
            publishedAt: recipe.publishedAt,
            status: recipe.status,
            title: recipe.title,
            content:
              lead && EditorState.createWithContent(convertFromRaw(lead)),
            ...getImageFromMedias(recipe.medias),
            medias,
            url: getRecipeUrl(recipe.brandKey, recipe.recipeUrls),
          };
        },
      );

      // eslint-disable-next-line react-hooks/rules-of-hooks
      const { data: campaignsData } = useGetCampaignsBlocks(blockItems || []);

      return {
        blockItems: blockItems?.map((blockItem: BlockItemType) => ({
          ...blockItem,
          campaigns: getBlockItemAttachedCampaigns(blockItem, campaignsData),
        })),
        loading: recipesLoading,
        error: recipeError,
        fetchMore: recipeFetchMore,
      };
    }
    default:
      return {
        blockItems: undefined,
        loading: false,
        error: new Error(`Unrecognized ResourceType: ${resourceType}`),
        fetchMore: () => Promise.reject(),
      };
  }
};
